import { useMemo, useRef, useState } from 'react';

import { PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { equipmentApi } from '@entities/equipment';
import { equipmentInventoryApi } from '@entities/equipmentInventory';
import { useMoveEquipmentInventoryQuantityContext } from '@features/moveEquipmentInventoryQuantity';

import {
    getInventoryPreviewIndex,
    INVENTORY_DND_TYPE,
    INVENTORY_DROPZONE_DND_TYPE,
    WAREHOUSE_DND_TYPE,
} from '../lib/dnd';
import {
    reorderInventoryInEquipmentCache,
    reorderWarehousesInEquipmentCache,
    partiallyMoveInventoryInEquipmentCache,
} from '../lib/equipmentCache';
import { getEquipmentDragData } from '../lib/equipmentCollisionDetection';
import {
    getEquipmentWarehouseViews,
    getFilteredInventoryDropIndex,
    getFilteredInventoryItems,
    normalizeEquipmentFilter,
} from '../lib/filter';

import type { DragEndEvent, DragOverEvent, DragStartEvent } from '@dnd-kit/core';
import type { EquipmentDataQueryCache, Warehouse } from '@entities/equipment';

interface InventoryDropPreview {
    warehouseId: number;
    index: number;
    actualIndex: number;
}

const EMPTY_WAREHOUSES: Warehouse[] = [];

export const useEquipmentBoard = (filterValue: string) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { requestMove } = useMoveEquipmentInventoryQuantityContext();
    const equipmentDataQueryKey = equipmentApi.getEquipmentDataQueryOptions().queryKey;
    const equipmentDataQuery = useQuery({
        ...equipmentApi.getEquipmentDataQueryOptions(),
        refetchOnMount: false,
    });
    const { data: equipmentData } = equipmentDataQuery;
    const warehouses = equipmentData?.equipment ?? EMPTY_WAREHOUSES;
    const hasFullAccess = equipmentData?.restrictions.hasFullAccess ?? false;
    const normalizedFilter = normalizeEquipmentFilter(filterValue);
    const isFiltering = normalizedFilter.length > 0;
    const warehouseViews = useMemo(
        () => getEquipmentWarehouseViews(warehouses, normalizedFilter),
        [normalizedFilter, warehouses]
    );
    const [activeWarehouseId, setActiveWarehouseId] = useState<number | null>(null);
    const [activeInventoryGuid, setActiveInventoryGuid] = useState<string | null>(null);
    const [inventoryDropPreview, setInventoryDropPreview] = useState<InventoryDropPreview | null>(
        null
    );
    const previousWarehouseEquipmentDataRef = useRef<EquipmentDataQueryCache | undefined>();
    const previousInventoryEquipmentDataRef = useRef<EquipmentDataQueryCache | undefined>();
    const inventoryDropPreviewRef = useRef<InventoryDropPreview | null>(null);
    const activeWarehouse = warehouses.find((warehouse) => warehouse.id === activeWarehouseId);
    const activeWarehouseView = warehouseViews.find(
        ({ warehouse }) => warehouse.id === activeWarehouseId
    );
    const activeInventory = warehouses
        .flatMap((warehouse) => warehouse.items)
        .find((inventory) => inventory.guid === activeInventoryGuid);
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        })
    );

    const updateInventoryDropPreview = (nextPreview: InventoryDropPreview | null) => {
        const currentPreview = inventoryDropPreviewRef.current;

        if (
            currentPreview?.warehouseId === nextPreview?.warehouseId &&
            currentPreview?.index === nextPreview?.index &&
            currentPreview?.actualIndex === nextPreview?.actualIndex
        ) {
            return;
        }

        inventoryDropPreviewRef.current = nextPreview;
        setInventoryDropPreview(nextPreview);
    };

    const sortWarehouseMutation = useMutation({
        mutationFn: equipmentApi.sortWarehouse,
        onError: () => {
            if (previousWarehouseEquipmentDataRef.current) {
                queryClient.setQueryData<EquipmentDataQueryCache>(
                    equipmentDataQueryKey,
                    previousWarehouseEquipmentDataRef.current
                );
            }
        },
        onSettled: () => {
            previousWarehouseEquipmentDataRef.current = undefined;
        },
    });

    const sortInventoryMutation = useMutation({
        mutationFn: equipmentApi.moveInventory,
        onError: () => {
            if (previousInventoryEquipmentDataRef.current) {
                queryClient.setQueryData<EquipmentDataQueryCache>(
                    equipmentDataQueryKey,
                    previousInventoryEquipmentDataRef.current
                );
            }
        },
        onSettled: (_data, _error, variables) => {
            const sourceWarehouseId =
                variables &&
                previousInventoryEquipmentDataRef.current?.data.response.equipment.find(
                    (warehouse) =>
                        warehouse.items.some((item) => item.guid === variables.inventoryGuid)
                )?.id;

            previousInventoryEquipmentDataRef.current = undefined;

            if (!variables) {
                return;
            }

            const shouldInvalidateSourceWarehouse =
                typeof sourceWarehouseId !== 'undefined' &&
                sourceWarehouseId !== variables.toWarehouseId;

            window.setTimeout(() => {
                void Promise.all([
                    queryClient.invalidateQueries({
                        queryKey: equipmentApi.getTotalInfoQueryOptions().queryKey,
                    }),
                    queryClient.invalidateQueries({
                        queryKey: equipmentApi.getWarehouseTotalInfoQueryOptions({
                            warehouseId: variables.toWarehouseId,
                        }).queryKey,
                    }),
                    ...(shouldInvalidateSourceWarehouse
                        ? [
                              queryClient.invalidateQueries({
                                  queryKey: equipmentApi.getWarehouseTotalInfoQueryOptions({
                                      warehouseId: sourceWarehouseId,
                                  }).queryKey,
                              }),
                          ]
                        : []),
                ]);
            }, 1000);
        },
    });

    const partiallyMoveInventoryMutation = useMutation({
        mutationFn: equipmentInventoryApi.partiallyMoveInventory,
        onMutate: async (variables) => {
            await queryClient.cancelQueries({ queryKey: equipmentDataQueryKey });

            const previousEquipmentData =
                queryClient.getQueryData<EquipmentDataQueryCache>(equipmentDataQueryKey);
            const sourceWarehouseId = previousEquipmentData?.data.response.equipment.find(
                (warehouse) => warehouse.items.some((item) => item.guid === variables.inventoryGuid)
            )?.id;

            return { previousEquipmentData, sourceWarehouseId };
        },
        onSuccess: (data, variables, context) => {
            if (typeof context?.sourceWarehouseId === 'undefined') {
                return;
            }

            queryClient.setQueryData<EquipmentDataQueryCache>(equipmentDataQueryKey, (previous) =>
                partiallyMoveInventoryInEquipmentCache({
                    previous,
                    inventoryGuid: variables.inventoryGuid,
                    fromWarehouseId: context.sourceWarehouseId,
                    toWarehouseId: variables.toWarehouseId,
                    newIndex: variables.sortIndex,
                    quantity: variables.quantity,
                    createdInventory: data.data.response,
                })
            );
        },
        onError: (_error, _variables, context) => {
            if (context?.previousEquipmentData) {
                queryClient.setQueryData(equipmentDataQueryKey, context.previousEquipmentData);
            }
        },
        onSettled: async (_data, _error, variables, context) => {
            if (!variables) {
                return;
            }

            await Promise.all([
                queryClient.invalidateQueries({
                    queryKey: equipmentApi.getTotalInfoQueryOptions().queryKey,
                }),
                queryClient.invalidateQueries({
                    queryKey: equipmentApi.getWarehouseTotalInfoQueryOptions({
                        warehouseId: variables.toWarehouseId,
                    }).queryKey,
                }),
                ...(typeof context?.sourceWarehouseId !== 'undefined'
                    ? [
                          queryClient.invalidateQueries({
                              queryKey: equipmentApi.getWarehouseTotalInfoQueryOptions({
                                  warehouseId: context.sourceWarehouseId,
                              }).queryKey,
                          }),
                      ]
                    : []),
            ]);
        },
    });

    const handleDragStart = async ({ active }: DragStartEvent) => {
        const activeData = getEquipmentDragData(active.data);

        if (activeData?.type === INVENTORY_DND_TYPE) {
            updateInventoryDropPreview(null);
            previousInventoryEquipmentDataRef.current =
                queryClient.getQueryData<EquipmentDataQueryCache>(equipmentDataQueryKey);
            setActiveInventoryGuid(activeData.inventoryGuid);
            await queryClient.cancelQueries({ queryKey: equipmentDataQueryKey }, { revert: false });

            return;
        }

        await queryClient.cancelQueries({ queryKey: equipmentDataQueryKey });

        if (activeData?.type === WAREHOUSE_DND_TYPE) {
            previousWarehouseEquipmentDataRef.current =
                queryClient.getQueryData<EquipmentDataQueryCache>(equipmentDataQueryKey);
            setActiveWarehouseId(activeData.warehouseId);
        }
    };

    const handleInventoryDragOver = ({ active, delta, over }: DragOverEvent) => {
        if (!over) {
            updateInventoryDropPreview(null);

            return;
        }

        const activeData = getEquipmentDragData(active.data);
        const overData = getEquipmentDragData(over.data);

        if (activeData?.type !== INVENTORY_DND_TYPE) {
            updateInventoryDropPreview(null);

            return;
        }

        const overWarehouseId =
            overData?.type === INVENTORY_DND_TYPE || overData?.type === INVENTORY_DROPZONE_DND_TYPE
                ? overData.warehouseId
                : undefined;

        if (typeof overWarehouseId === 'undefined') {
            updateInventoryDropPreview(null);

            return;
        }

        const currentEquipmentData =
            queryClient.getQueryData<EquipmentDataQueryCache>(equipmentDataQueryKey);
        const currentWarehouses = currentEquipmentData?.data.response.equipment ?? [];
        const sourceWarehouse = currentWarehouses.find((warehouse) =>
            warehouse.items.some((item) => item.guid === activeData.inventoryGuid)
        );
        const overWarehouse = currentWarehouses.find(
            (warehouse) => warehouse.id === overWarehouseId
        );

        if (!sourceWarehouse || !overWarehouse) {
            updateInventoryDropPreview(null);

            return;
        }

        const visibleSourceWarehouseItems = getFilteredInventoryItems(
            sourceWarehouse.items,
            normalizedFilter
        );
        const visibleOverWarehouseItems = getFilteredInventoryItems(
            overWarehouse.items,
            normalizedFilter
        );
        const activeInventoryIndex = visibleSourceWarehouseItems.findIndex(
            (item) => item.guid === activeData.inventoryGuid
        );
        const overInventoryIndex =
            overData?.type === INVENTORY_DND_TYPE
                ? visibleOverWarehouseItems.findIndex(
                      (item) => item.guid === overData.inventoryGuid
                  )
                : undefined;

        if (
            activeInventoryIndex === -1 ||
            (overData?.type === INVENTORY_DND_TYPE && overInventoryIndex === -1)
        ) {
            updateInventoryDropPreview(null);

            return;
        }

        const activeRect = active.rect.current.translated ?? active.rect.current.initial;
        const activeCenterY = activeRect
            ? activeRect.top +
              (active.rect.current.translated ? 0 : delta.y) +
              activeRect.height / 2
            : undefined;
        const isAfterOverItem =
            overData?.type === INVENTORY_DND_TYPE &&
            typeof activeCenterY !== 'undefined' &&
            activeCenterY > over.rect.top + over.rect.height / 2;
        const isSameWarehouse = sourceWarehouse.id === overWarehouseId;
        const previewIndex = getInventoryPreviewIndex({
            overIndex: overInventoryIndex,
            overWarehouseItemsCount: visibleOverWarehouseItems.length,
            isSameWarehouse,
            isAfterOverItem,
        });
        const actualIndex = isFiltering
            ? getFilteredInventoryDropIndex({
                  warehouseItems: overWarehouse.items,
                  visibleItems: visibleOverWarehouseItems,
                  activeInventoryGuid: activeData.inventoryGuid,
                  visibleIndex: previewIndex,
              })
            : previewIndex;

        if (!isSameWarehouse) {
            queryClient.setQueryData<EquipmentDataQueryCache>(equipmentDataQueryKey, (previous) =>
                reorderInventoryInEquipmentCache({
                    previous,
                    inventoryGuid: activeData.inventoryGuid,
                    fromWarehouseId: sourceWarehouse.id,
                    toWarehouseId: overWarehouseId,
                    newIndex: actualIndex,
                })
            );
        }

        updateInventoryDropPreview({
            warehouseId: overWarehouseId,
            index: previewIndex,
            actualIndex,
        });
    };

    const handleDragOver = (event: DragOverEvent) => {
        const { active, over } = event;
        const activeData = getEquipmentDragData(active.data);
        const overData = over ? getEquipmentDragData(over.data) : undefined;

        if (activeData?.type === INVENTORY_DND_TYPE) {
            handleInventoryDragOver(event);

            return;
        }

        if (!over || active.id === over.id || activeData?.type !== WAREHOUSE_DND_TYPE) {
            return;
        }

        const currentEquipmentData =
            queryClient.getQueryData<EquipmentDataQueryCache>(equipmentDataQueryKey);
        const currentWarehouses = currentEquipmentData?.data.response.equipment ?? [];
        const overWarehouseId =
            overData?.type === WAREHOUSE_DND_TYPE ||
            overData?.type === INVENTORY_DND_TYPE ||
            overData?.type === INVENTORY_DROPZONE_DND_TYPE
                ? overData.warehouseId
                : undefined;

        if (typeof overWarehouseId === 'undefined') {
            return;
        }

        const oldIndex = currentWarehouses.findIndex(
            (warehouse) => warehouse.id === activeData.warehouseId
        );
        const newIndex = currentWarehouses.findIndex(
            (warehouse) => warehouse.id === overWarehouseId
        );

        if (oldIndex === -1 || newIndex === -1) {
            return;
        }

        queryClient.setQueryData<EquipmentDataQueryCache>(equipmentDataQueryKey, (previous) =>
            reorderWarehousesInEquipmentCache({
                previous,
                oldIndex,
                newIndex,
            })
        );
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        const activeData = getEquipmentDragData(active.data);
        const inventoryDropPreviewAtEnd = inventoryDropPreviewRef.current;

        setActiveWarehouseId(null);
        setActiveInventoryGuid(null);
        updateInventoryDropPreview(null);

        if (!over) {
            if (
                activeData?.type === INVENTORY_DND_TYPE &&
                previousInventoryEquipmentDataRef.current
            ) {
                queryClient.setQueryData<EquipmentDataQueryCache>(
                    equipmentDataQueryKey,
                    previousInventoryEquipmentDataRef.current
                );
                previousInventoryEquipmentDataRef.current = undefined;

                return;
            }

            if (previousWarehouseEquipmentDataRef.current) {
                queryClient.setQueryData<EquipmentDataQueryCache>(
                    equipmentDataQueryKey,
                    previousWarehouseEquipmentDataRef.current
                );
                previousWarehouseEquipmentDataRef.current = undefined;
            }

            return;
        }

        if (activeData?.type === INVENTORY_DND_TYPE) {
            const currentEquipmentData =
                queryClient.getQueryData<EquipmentDataQueryCache>(equipmentDataQueryKey);
            const currentWarehouses = currentEquipmentData?.data.response.equipment ?? [];
            const sourceWarehouse = currentWarehouses.find((warehouse) =>
                warehouse.items.some((item) => item.guid === activeData.inventoryGuid)
            );
            const targetWarehouse = currentWarehouses.find(
                (warehouse) => warehouse.id === inventoryDropPreviewAtEnd?.warehouseId
            );
            const activeInventoryIndex =
                sourceWarehouse?.items.findIndex(
                    (item) => item.guid === activeData.inventoryGuid
                ) ?? -1;

            if (
                !inventoryDropPreviewAtEnd ||
                !sourceWarehouse ||
                !targetWarehouse ||
                activeInventoryIndex === -1
            ) {
                if (previousInventoryEquipmentDataRef.current) {
                    queryClient.setQueryData<EquipmentDataQueryCache>(
                        equipmentDataQueryKey,
                        previousInventoryEquipmentDataRef.current
                    );
                }
                previousInventoryEquipmentDataRef.current = undefined;

                return;
            }

            const maxIndex =
                sourceWarehouse.id === targetWarehouse.id
                    ? Math.max(0, targetWarehouse.items.length - 1)
                    : targetWarehouse.items.length;
            const newIndex = Math.min(Math.max(inventoryDropPreviewAtEnd.actualIndex, 0), maxIndex);

            if (sourceWarehouse.id !== targetWarehouse.id || activeInventoryIndex !== newIndex) {
                queryClient.setQueryData<EquipmentDataQueryCache>(
                    equipmentDataQueryKey,
                    (previous) =>
                        reorderInventoryInEquipmentCache({
                            previous,
                            inventoryGuid: activeData.inventoryGuid,
                            fromWarehouseId: sourceWarehouse.id,
                            toWarehouseId: targetWarehouse.id,
                            newIndex,
                        })
                );
            }

            const previousWarehouse =
                previousInventoryEquipmentDataRef.current?.data.response.equipment.find(
                    (warehouse) =>
                        warehouse.items.some((item) => item.guid === activeData.inventoryGuid)
                );
            const previousIndex =
                previousWarehouse?.items.findIndex(
                    (item) => item.guid === activeData.inventoryGuid
                ) ?? -1;

            if (targetWarehouse.id === previousWarehouse?.id && newIndex === previousIndex) {
                previousInventoryEquipmentDataRef.current = undefined;

                return;
            }

            const movePayload = {
                inventoryGuid: activeData.inventoryGuid,
                toWarehouseId: targetWarehouse.id,
                newIndex,
            };
            const previousEquipmentData = previousInventoryEquipmentDataRef.current;
            const previousInventory = previousWarehouse?.items.find(
                (item) => item.guid === activeData.inventoryGuid
            );

            if (
                previousEquipmentData &&
                previousWarehouse &&
                previousWarehouse.id !== targetWarehouse.id &&
                previousInventory &&
                previousInventory.amount > 1
            ) {
                requestMove({
                    amount: previousInventory.amount,
                    onMoveFully: () => {
                        previousInventoryEquipmentDataRef.current = previousEquipmentData;
                        queryClient.setQueryData<EquipmentDataQueryCache>(
                            equipmentDataQueryKey,
                            reorderInventoryInEquipmentCache({
                                previous: previousEquipmentData,
                                inventoryGuid: activeData.inventoryGuid,
                                fromWarehouseId: previousWarehouse.id,
                                toWarehouseId: targetWarehouse.id,
                                newIndex,
                            })
                        );

                        return sortInventoryMutation.mutateAsync(movePayload);
                    },
                    onMovePartially: (quantity) => {
                        queryClient.setQueryData<EquipmentDataQueryCache>(
                            equipmentDataQueryKey,
                            previousEquipmentData
                        );
                        previousInventoryEquipmentDataRef.current = undefined;

                        return partiallyMoveInventoryMutation.mutateAsync({
                            inventoryGuid: activeData.inventoryGuid,
                            toWarehouseId: targetWarehouse.id,
                            sortIndex: newIndex,
                            quantity,
                        });
                    },
                    onCancel: () => {
                        queryClient.setQueryData<EquipmentDataQueryCache>(
                            equipmentDataQueryKey,
                            previousEquipmentData
                        );
                        previousInventoryEquipmentDataRef.current = undefined;
                    },
                });

                return;
            }

            sortInventoryMutation.mutate(movePayload);

            return;
        }

        if (activeData?.type !== WAREHOUSE_DND_TYPE) {
            return;
        }

        const currentEquipmentData =
            queryClient.getQueryData<EquipmentDataQueryCache>(equipmentDataQueryKey);
        const newIndex =
            currentEquipmentData?.data.response.equipment.findIndex(
                (warehouse) => warehouse.id === activeData.warehouseId
            ) ?? -1;
        const previousIndex =
            previousWarehouseEquipmentDataRef.current?.data.response.equipment.findIndex(
                (warehouse) => warehouse.id === activeData.warehouseId
            ) ?? -1;

        if (newIndex === -1 || newIndex === previousIndex) {
            previousWarehouseEquipmentDataRef.current = undefined;

            return;
        }

        sortWarehouseMutation.mutate({
            warehouseId: activeData.warehouseId,
            newIndex,
        });
    };

    const handleDragCancel = () => {
        setActiveWarehouseId(null);
        setActiveInventoryGuid(null);
        updateInventoryDropPreview(null);

        if (previousInventoryEquipmentDataRef.current) {
            queryClient.setQueryData<EquipmentDataQueryCache>(
                equipmentDataQueryKey,
                previousInventoryEquipmentDataRef.current
            );
            previousInventoryEquipmentDataRef.current = undefined;
        }

        if (previousWarehouseEquipmentDataRef.current) {
            queryClient.setQueryData<EquipmentDataQueryCache>(
                equipmentDataQueryKey,
                previousWarehouseEquipmentDataRef.current
            );
            previousWarehouseEquipmentDataRef.current = undefined;
        }
    };

    return {
        t,
        equipmentData,
        isEquipmentLoading: equipmentDataQuery.isLoading,
        isEquipmentError: equipmentDataQuery.isError,
        warehouses,
        hasFullAccess,
        isFiltering,
        warehouseViews,
        activeInventoryGuid,
        inventoryDropPreview,
        activeWarehouse,
        activeWarehouseView,
        activeInventory,
        sensors,
        isInventoryDragDisabled:
            sortInventoryMutation.isPending || partiallyMoveInventoryMutation.isPending,
        handleDragStart,
        handleDragOver,
        handleDragEnd,
        handleDragCancel,
    };
};
